import { createAsyncThunk } from '@reduxjs/toolkit';

import callApi from '../../service/callApi';
import sendRequest from '../../service/sendRequest';
import { somethingWrongNotification } from '../notifier/notifierActions';

const GET_RESONANCE_PROJECT_INFO = 'projects/resonance/GET_RESONANCE_PROJECT_INFO';
const GET_RESONANCE_INIT_DATA = 'projects/resonance/GET_RESONANCE_INIT_DATA';
const GET_FILTERED_MESSAGES = 'projects/resonance/GET_FILTERED_MESSAGES';
const GET_SINGLE_MESSAGE_CORRELATIONS = 'projects/resonance/GET_SINGLE_MESSAGE_CORRELATIONS';
const GET_RESONANCE_CONCEPTS = 'projects/resonance/GET_RESONANCE_CONCEPTS';
const GET_RESONANCE_H2H_CONCEPTS = 'projects/resonance/GET_RESONANCE_H2H_CONCEPTS';
const GET_RESONANCE_BACKGROUND_DATA = 'projects/resonance/GET_RESONANCE_BACKGROUND_DATA';

export const getResonanceProjectInfo = createAsyncThunk(
  GET_RESONANCE_PROJECT_INFO,
  async (id, { dispatch, rejectWithValue }) => {
    const response = await sendRequest(() => callApi.get(`/api/resonance/data?id=${id}`), {
      onError: (e) => {
        dispatch(somethingWrongNotification());
        return rejectWithValue(e);
      },
    });

    return response.data;
  },
);

export const getResonanceInitData = createAsyncThunk(
  GET_RESONANCE_INIT_DATA,
  async (id, { dispatch, rejectWithValue }) => {
    const response = await sendRequest(() => callApi.get(`/api/resonance/calculations/init/data/${id}`), {
      onError: (e) => {
        dispatch(somethingWrongNotification());
        return rejectWithValue(e);
      },
    });

    return response.data;
  },
);

export const getFilteredMessages = createAsyncThunk(
  GET_FILTERED_MESSAGES,
  async ({ projectId, appliedFilters }, { dispatch, rejectWithValue }) => {
    if (!appliedFilters.length || !projectId) {
      return rejectWithValue(null);
    }

    const audienceIds = appliedFilters.map((filter) => filter.id);

    const response = await sendRequest(
      () => callApi.post(`/api/resonance/calculations/filter/messages?projectId=${projectId}`, audienceIds),
      {
        onError: (e) => {
          dispatch(somethingWrongNotification());
          return rejectWithValue(e);
        },
      },
    );

    return response.data;
  },
);

export const getSingleMessageCorrelations = createAsyncThunk(
  GET_SINGLE_MESSAGE_CORRELATIONS,
  async ({ projectId, messageId }, { dispatch, rejectWithValue }) => {
    const response = await sendRequest(
      () => callApi.post(`/api/resonance/calculations/high/performing?projectId=${projectId}&messageId=${messageId}`),
      {
        onError: (e) => {
          dispatch(somethingWrongNotification());
          return rejectWithValue(e);
        },
      },
    );

    return response.data;
  },
);

export const getResonanceConcepts = createAsyncThunk(
  GET_RESONANCE_CONCEPTS,
  async ({ projectId, audiences }, { dispatch, rejectWithValue }) => {
    const audienceIds = audiences.map((audience) => audience.id);

    const response = await sendRequest(
      () => callApi.post(`/api/resonance/calculations/concepts?projectId=${projectId}`, audienceIds),
      {
        onError: (e) => {
          dispatch(somethingWrongNotification());
          return rejectWithValue(e);
        },
      },
    );

    const { averages, concepts, respondents } = response.data;

    return { averages, concepts, respondents };
  },
);

export const getResonanceH2HConcepts = createAsyncThunk(
  GET_RESONANCE_H2H_CONCEPTS,
  async ({ projectId, audiences }, { dispatch, rejectWithValue }) => {
    const audienceIds = audiences.map((audience) => audience.id);

    const response = await sendRequest(
      () => callApi.post(`/api/resonance/calculations/h2h/concepts?projectId=${projectId}`, audienceIds),
      {
        onError: (e) => {
          dispatch(somethingWrongNotification());
          return rejectWithValue(e);
        },
      },
    );

    return response.data;
  },
);

export const getResonanceBackgroundData = createAsyncThunk(
  GET_RESONANCE_BACKGROUND_DATA,
  async ({ projectId, audiences, header }, { dispatch, rejectWithValue }) => {
    const audienceIds = audiences.map((audience) => audience.id);

    const response = await sendRequest(
      () => callApi.post(`/api/resonance/calculations/background?projectId=${projectId}&header=${header}`, audienceIds),
      {
        onError: (e) => {
          dispatch(somethingWrongNotification());
          return rejectWithValue(e);
        },
      },
    );

    return response.data;
  },
);
